import React, { useEffect, useState } from "react";
import {
  Box, Button,
  Grid,
  Input,
  InputAdornment,
  Select,
  TextareaAutosize ,
  Typography,
  MenuItem,
} from "@mui/material";
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CalendarMonth as CalendarMonthIcon } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {styled} from "@mui/material/styles";

const InitiativeForm = ({ onFormSubmit }) => {
  const [initiativeName, setInitiativeName] = useState("");
  const [priority, setPriority] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [description, setDescription] = useState("");
  //FGIV1AI -- Add new ack text state
  const [personalize, setPersonalize] = useState("");
  const [isAckSwitchOn, setIsAckSwitchOn] = useState(true);
  const [attachmentsBlobs, setAttachmentsBlobs] = useState([]); //array of blobs


  const formatDate = (date) => {
    if (!date) return;
    return new Date(date).toLocaleDateString("en-US");
  };

  const handleSwitchChange = (event) => {
    setIsAckSwitchOn(event.target.checked);
    if (!event.target.checked) {
      setDueDate(null);
    }
  };

  const handleChange = (event) => {
    setPriority(event.target.value);
  };


  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setAttachmentsBlobs((prev) => [...prev, file]);
  };

  useEffect(() => {
    // verify if the form is valid with the required info
    const isFormValid =
      initiativeName.trim() !== "" &&
      priority !== "" &&
      description.trim() !== "" &&
      (!isAckSwitchOn || dueDate !== null);

    const formData = {
      title: initiativeName,
      priority: parseInt(priority, 10),
      notification_text: description,
      due_date: isAckSwitchOn ? formatDate(dueDate) : null,
      acknowledgement_custom_text: isAckSwitchOn ? personalize : null,
      isValid: isFormValid
    };

    onFormSubmit(formData, attachmentsBlobs);
  }, [initiativeName, priority, dueDate, description, isAckSwitchOn, attachmentsBlobs, personalize]);

  return (
    <Box>
      <Typography gutterBottom variant="h3" color="grey">
        Add the information for the new initiatives
      </Typography>

      <Grid container alignItems="flex-start" spacing={8} pt={5}>
        <Grid item xs={3}>
          <Typography gutterBottom variant="h3">
            Initiative name <span style={{ color: "red" }}>*</span>
          </Typography>
          <Input
            required
            id="initiative-name"
            value={initiativeName}
            onChange={(e) => setInitiativeName(e.target.value)}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography gutterBottom variant="h3">
            Priority <span style={{ color: "red" }}>*</span>
          </Typography>
          <Select
                id="Priority"
                name="Priority"
                value={priority}
                onChange={handleChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span>Select priority</span>;
                  }

                  switch (selected) {
                    case '2':
                      return 'High';
                    case '1':
                      return 'Medium';
                    case '0':
                      return 'Low';
                    default:
                      return selected;
                  }
                }}
                size="small"
              >
                <MenuItem value="2">High</MenuItem>
                <MenuItem value="1">Medium</MenuItem>
                <MenuItem value="0">Low</MenuItem>
            </Select>
        </Grid>
      {/*FGIV1AI -- Add new acknowledment text field and make it required if the switch is on*/}
      </Grid>
      <Grid container pt={5}>
        <Typography gutterBottom variant="h3">
          Description <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextareaAutosize
          minRows={5}

          style={{ width: "100%" }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Grid>

      <Grid container alignItems="flex-start" spacing={8} pt={5}>
        <Grid item size={{xs: 12, md:2.5}} >
          <Typography gutterBottom variant="h3">
            <Box display="flex" alignItems="center">
              Acknowledgement <span style={{ color: "red" }}>*</span>
              <Tooltip
                title="If disabled, the recipient will not be prompted to confirm the notification."
                placement="top"
              >
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  tabIndex={0}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </Box>
              </Tooltip>
            </Box>
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={isAckSwitchOn}
                onChange={handleSwitchChange}
                name="controlled"
              />
            }
            label="Off/On"
            labelPlacement="start"
          />
        
        
        </Grid>

      <Grid item size={{xs:12, md:9.5}}>
        <Typography gutterBottom variant="h3"  style={{ color: !isAckSwitchOn ? "#cccccc" : "inherit" }}>
            Personalize Acknowledgement 
            <span style={{ color: isAckSwitchOn ? "red" : "#cccccc" }}>*</span>
          </Typography>
          
           <Input
            required={isAckSwitchOn}
            disabled={!isAckSwitchOn}
            value={personalize}
            placeholder="I acknowledge and will make the necessary adjustments"
            onChange={(e) => setPersonalize(e.target.value)}
            style={{ width: "100%" }}
          /> 
          </Grid>

      </Grid>

      <Grid 
          item
          xs={5} 
          sx={{
            color: !isAckSwitchOn ? "#cccccc" : "inherit",
            pointerEvents: !isAckSwitchOn ? "none" : "auto",
            mt: 6
          }}
        >
          <Typography
            gutterBottom
            variant="h3"
            style={{ color: !isAckSwitchOn ? "#cccccc" : "inherit" }}
          >
            Due Date
            <span style={{ color: isAckSwitchOn ? "red" : "#cccccc" }}>*</span>
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
            required={isAckSwitchOn}
            value={dueDate}
            onChange={(date) => {
              if (
                !date ||
                typeof date === "string" ||
                isNaN(new Date(date).getTime())
              ) {
                setDueDate(null);
              } else {
                setDueDate(date);
              }
            }}
            disabled={!isAckSwitchOn}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthIcon />
                </InputAdornment>
              ),
              style: { color: !isAckSwitchOn ? "#cccccc" : "inherit" },
            }}
            placeholder="Select a date"
          />
          </LocalizationProvider>
        
        </Grid>
        
        



      <Grid container pt={5}>
        <Grid item size={{xs:12}}>
        <Typography gutterBottom variant="h3">
          Attachments
        </Typography>
        </Grid>
        <Grid item size={{xs:12}}>
          <Button
              sx={{ color: "#528b47", borderColor: "#528b47" }}
              component="label"
              role={undefined}
              variant="secondary"
              startIcon={<CloudUploadIcon />}
          >
            Upload files
            <VisuallyHiddenInput
                type="file"
                onChange={handleFileUpload}
            />
          </Button>
        </Grid>

        <Grid item size={{xs:12}} mt={2}>
          {attachmentsBlobs.length > 0 && (
              <ul>
                {attachmentsBlobs.map((file, index) => (
                    <li key={index}>{file.name}</li>
                ))}
              </ul>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default InitiativeForm;
